import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import {
  getClasses,
  getTeacherStudents,
  createAssignment,
  getResourceAssignments
} from '../../services/api';
import './AssignmentModal.css';

const AssignmentModal = ({ isOpen, onClose, resourceType, resourceId, onAssignmentCreated }) => {
  const { handleRequest } = useApi();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [classFilter, setClassFilter] = useState('all'); // Filter for student list
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [visibleFrom, setVisibleFrom] = useState('');

  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        try {
          const [classesResponse, studentsResponse] = await Promise.all([
            handleRequest(getClasses).catch(() => ({ data: [] })),
            handleRequest(getTeacherStudents).catch(() => ({ data: [] }))
          ]);
          setClasses(classesResponse.data || []);
          setStudents(studentsResponse.data || []);
        } catch (err) {
          setError('Kunne ikke laste inn klasser og elever');
        }
      };
      fetchData();
    } else {
      // Reset state when modal closes
      setSelectedClasses([]);
      setSelectedStudents([]);
      setMessage('');
      setError('');
      setClassFilter('all');
      setScheduleEnabled(false);
      setVisibleFrom('');
    }
  }, [isOpen, handleRequest]);

  const toggleClass = (classId) => {
    setSelectedClasses(prev =>
      prev.includes(classId)
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };

  const toggleStudent = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedClasses.length === 0 && selectedStudents.length === 0) {
      setError('Velg minst én klasse eller elev');
      return;
    }

    let visibleFromValue = null;
    if (scheduleEnabled) {
      if (!visibleFrom) {
        setError('Velg dato og tidspunkt for synlighet');
        return;
      }
      const parsedVisibleFrom = new Date(visibleFrom);
      if (Number.isNaN(parsedVisibleFrom.getTime())) {
        setError('Ugyldig dato for synlighet');
        return;
      }
      visibleFromValue = parsedVisibleFrom.toISOString();
    }

    setLoading(true);
    setError('');

    try {
      await handleRequest(createAssignment, {
        resourceType,
        resourceId,
        classIds: selectedClasses,
        studentIds: selectedStudents,
        message: message.trim(),
        visibleFrom: visibleFromValue
      });

      if (onAssignmentCreated) {
        onAssignmentCreated();
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Kunne ikke opprette tildeling');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Tildel {resourceType === 'Lesson' ? 'quiz' : resourceType === 'ProgrammingLesson' ? 'programmeringsleksjon' : 'interaktiv leksjon'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="assignment-form">
          {error && (
            <div className="assignment-error">{error}</div>
          )}

          <div className="form-section">
            <h3>Velg klasser</h3>
            {classes.length === 0 ? (
              <p className="empty-state">Ingen klasser tilgjengelig</p>
            ) : (
              <div className="selection-list">
                {classes.map((classroom) => (
                  <label key={classroom._id || classroom.id} className="selection-item">
                    <input
                      type="checkbox"
                      checked={selectedClasses.includes(classroom._id || classroom.id)}
                      onChange={() => toggleClass(classroom._id || classroom.id)}
                    />
                    <span>{classroom.name}</span>
                    <span className="class-code-badge">{classroom.joinCode}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <h3>Velg elever</h3>
              {classes.length > 0 && (
                <select
                  className="class-filter-select"
                  value={classFilter}
                  onChange={(e) => setClassFilter(e.target.value)}
                >
                  <option value="all">Alle klasser</option>
                  {classes.map((classroom) => (
                    <option key={classroom._id || classroom.id} value={classroom._id || classroom.id}>
                      {classroom.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {students.length === 0 ? (
              <p className="empty-state">Ingen elever tilgjengelig</p>
            ) : (
              <div className="selection-list">
                {students
                  .filter((student) => {
                    if (classFilter === 'all') return true;
                    const studentClassIds = (student.classIds || []).map(id => String(id));
                    return studentClassIds.includes(String(classFilter));
                  })
                  .map((student) => {
                    const studentClassIds = (student.classIds || []).map(id => String(id));
                    const studentClasses = classes
                      .filter(c => studentClassIds.includes(String(c._id || c.id)))
                      .map(c => c.name)
                      .join(', ');
                    
                    return (
                      <label key={student._id || student.id} className="selection-item">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student._id || student.id)}
                          onChange={() => toggleStudent(student._id || student.id)}
                        />
                        <span>{student.name}</span>
                        <span className="student-email-badge">{student.email}</span>
                        {studentClasses && (
                          <span className="student-class-badge">{studentClasses}</span>
                        )}
                      </label>
                    );
                  })}
                {students.filter((student) => {
                  if (classFilter === 'all') return true;
                  const studentClassIds = (student.classIds || []).map(id => String(id));
                  return studentClassIds.includes(String(classFilter));
                }).length === 0 && (
                  <p className="empty-state">Ingen elever i denne klassen</p>
                )}
              </div>
            )}
          </div>

          <div className="form-section">
            <label htmlFor="assignment-message">Melding (valgfritt)</label>
            <textarea
              id="assignment-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Legg til en melding til elevene..."
              rows="3"
            />
          </div>

          <div className="form-section">
            <h3>Synlighet</h3>
            <label className="visibility-toggle">
              <input
                type="checkbox"
                checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.target.checked)}
              />
              <span>Planlegg synlighetstidspunkt</span>
            </label>
            {scheduleEnabled && (
              <div className="visibility-input">
                <label htmlFor="assignment-visible-from">Synlig fra</label>
                <input
                  type="datetime-local"
                  id="assignment-visible-from"
                  value={visibleFrom}
                  onChange={(e) => setVisibleFrom(e.target.value)}
                />
                <p className="visibility-help">Elever ser ikke tildelingen før tidspunktet.</p>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose} disabled={loading}>
              Avbryt
            </button>
            <button type="submit" className="submit-btn" disabled={loading || (selectedClasses.length === 0 && selectedStudents.length === 0)}>
              {loading ? 'Tildeler...' : 'Tildel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignmentModal;
